import { Check, LucideIcon, Pencil, X } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/v2/button";
import {
  Card,
  CardAction,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/v2/card";

type ClientConfigCardProps = {
  title: string;
  icon: LucideIcon;
  isEditMode: boolean;
  isPending: boolean;
  onEdit: () => void;
  onCancel: () => void;
  children: React.ReactNode;
  editContent?: React.ReactNode;
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
};

const ClientConfigCard: React.FC<ClientConfigCardProps> = ({
  title,
  icon: Icon,
  isEditMode,
  isPending,
  onEdit,
  onCancel,
  children,
  editContent,
  onSubmit,
}) => {
  const { t } = useTranslation();

  const content = (
    <>
      <CardContent>
        {isEditMode ? editContent || children : children}
      </CardContent>
      {isEditMode && (
        <CardFooter className="flex gap-4 pt-8">
          <Button
            disabled={isPending}
            type="submit"
          >
            <Check className="h-4 w-4 mr-2" />
            {isPending
              ? t("common.saving", "Saving...")
              : t("common.saveChanges", "Save Changes")}
          </Button>
          <Button variant="outline" onClick={onCancel} type="button">
            <X className="h-4 w-4 mr-2" />
            {t("common.cancel", "Cancel")}
          </Button>
        </CardFooter>
      )}
    </>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
            <Icon className="h-5 w-5 text-primary" />
          </div>
          <CardTitle>{title}</CardTitle>
        </div>
        {!isEditMode && (
          <CardAction>
            <Button variant="ghost" onClick={onEdit}>
              <Pencil className="h-4 w-4 mr-2" />
              {t("common.editDetails", "Edit Details")}
            </Button>
          </CardAction>
        )}
      </CardHeader>
      {isEditMode && onSubmit ? (
        <form
          onSubmit={(e) => {
            onSubmit(e);
          }}
        >
          {content}
        </form>
      ) : (
        content
      )}
    </Card>
  );
};

export default ClientConfigCard;
